import { cn } from '../../lib/css/className/className';
import './typography.css';
import { Color } from '../../internal/types/designSystemTypes';

const cls = cn('typography');

export type TypographyTextPreset =
    | '10-12'
    | '10-12-M'
    | '12-16'
    | '12-16-M'
    | '14-20'
    | '14-20-M'
    | '16-20'
    | '16-20-M'
    | '16-24'
    | '16-24-M'
    | '20-28'
    | '20-28-M'
    | '24-32'
    | '24-32-M'
    | '32-40'
    | '32-40-M'
    | '12-16 + 12-16'
    | '12-16-M + 12-16'
    | '14-20 + 12-16'
    | '14-20-M + 12-16'
    | '14-20 + 14-20'
    | '14-20-M + 14-20'
    | '16-20 + 12-16'
    | '16-20-M + 12-16'
    | '16-24 + 12-16'
    | '16-24 + 16-24'
    | '16-24-M + 12-16'
    | '16-24-M + 16-24'
    | '20-28-M + 16-24'
    | '20-28 + 20-28'
    | '20-28-M + 20-28'
    | '24-32-M + 16-24'
    | '32-40-M + 20-28';

export type TypographyRole = 'main' | 'supplementary' | 'placeholder';

export interface TypographyOptions {
    /**
     * Цветовая палитра для текста и описания.
     * Перечисленные цвета определены дизайн-системой
     */
    color?: Color | 'current';

    /**
     * Размер шрифта и высота строки для текста.
     * Значения с парами предназначены для задания стиля основному тексту и описанию.
     * Перечисленные сочетания размеров определены дизайн-системой
     */
    size?: TypographyTextPreset;

    /**
     * Переключает декорирование для интеративного текста (при наведении на который всплывают popup). Не для ссылок.
     * Применяется в следующих случаях: https://nda.ya.ru/t/pKMtdVcQ753mav
     */
    interactive?: boolean;

    /**
     * Роль элемента типографики: основной (main) или дополнительный (supplementary) текст.
     * От роли зависят возвращаемые textClasses для одиночного текстового элемента
     * @default 'main'
     */
    role?: TypographyRole;

    /**
     * Жирность текста
     */
    bold?: boolean;
}

/**
 * React Hook для применения стилей типографики
 * Задает свойства:
 * - font-family
 * - font-size
 * - font-weight
 * - line-height
 * - color
 * - text-decoration
 */
export const useTypography = (options: TypographyOptions) => {
    const { size, color, interactive, role = 'main', bold } = options;
    const [mainTextSize, captionSize] = size?.split(' + ') ?? [];

    const textClasses = cls({ size: mainTextSize, role: role, color: color, interactive, bold });

    if (!captionSize) {
        return { textClasses };
    }

    const captionClasses = cls({ size: captionSize, role: 'supplementary', color: color });
    return { textClasses, captionClasses };
};
